import React, { useState, useEffect } from 'react';
import { Search, Calendar, Clock, ArrowRight, Filter, Tag, TrendingUp, FileText, User, Eye } from 'lucide-react';
import Layout from '../layouts/Layout';
import { Link } from 'react-router-dom';
import apiClient from '../helpers/apiClient';

const NewsPage = () => {
  const [articles, setArticles] = useState([]);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [filteredArticles, setFilteredArticles] = useState([]);

  useEffect(() => {
    loadNews();
  }, []);

  useEffect(() => {
    filterArticles();
    // eslint-disable-next-line
  }, [articles, searchTerm, selectedCategory]);

  const loadNews = async () => {
    try {
      setLoading(true);
      
      // Load featured articles
      const featuredResponse = await apiClient.getFeaturedNews(3);
      if (featuredResponse.success) {
        const sortedFeaturedArticles = (featuredResponse.data || []).sort(
          (a, b) => new Date(b.published_at) - new Date(a.published_at)
        );
        setFeaturedArticles(sortedFeaturedArticles);
      }

      // Load all articles
      const allResponse = await apiClient.getNews(false, 20);
      if (allResponse.success) {
        const sortedArticles = (allResponse.data || []).sort(
          (a, b) => new Date(b.published_at) - new Date(a.published_at)
        );
        setArticles(sortedArticles);
      }
      
    } catch (error) {
      console.error('Error loading news:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterArticles = () => {
    let filtered = articles;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(article =>
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.excerpt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (article.tags && article.tags.some(tag => 
          tag.toLowerCase().includes(searchTerm.toLowerCase())
        ))
      );
    }

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter(article => article.category === selectedCategory);
    }

    setFilteredArticles(filtered);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return '';
    }
  };

  const getReadingTime = (readingTime) => {
    if (!readingTime) return '';
    return `${readingTime} min read`;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Technology': 'bg-blue-100 text-blue-800',
      'Strategy': 'bg-purple-100 text-purple-800',
      'Compliance': 'bg-red-100 text-red-800',
      'Design': 'bg-green-100 text-green-800',
      'Development': 'bg-indigo-100 text-indigo-800',
      'News': 'bg-gray-100 text-gray-800',
      'Tutorial': 'bg-orange-100 text-orange-800',
      'Case Study': 'bg-teal-100 text-teal-800',
      'Industry': 'bg-yellow-100 text-yellow-800'
    };
    return colors[category] || 'bg-slate-100 text-slate-800';
  };

  const categories = [
    'Technology', 'Strategy', 'Compliance', 'Design', 'Development', 
    'News', 'Tutorial', 'Case Study', 'Industry'
  ];

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
  };

  return (
    <Layout
      title="News & Insights"
      description="Latest insights, tutorials, and industry news from KGC Innovations. Stay updated with web development trends, technology guides, and business strategies."
      keywords="web development news, technology insights, business strategy, development tutorials, industry trends"
      path="/news"
    >
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-white border border-slate-200 rounded-full text-sm text-slate-700 mb-6">
              <FileText className="w-4 h-4 mr-2 text-slate-500" />
              Latest Insights & Industry News
            </div>
            
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              News & Insights
            </h1>
            
            <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
              Stay ahead with expert insights, industry trends, and practical guides from our team of technology specialists.
            </p>

            {/* Search and Filter */}
            <div className="max-w-2xl mx-auto">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search articles..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                  />
                </div>
                
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="appearance-none pl-10 pr-8 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent bg-white min-w-[150px]"
                  >
                    <option value="">All Categories</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              </div>

              {(searchTerm || selectedCategory) && (
                <div className="flex items-center justify-center mt-4">
                  <button
                    onClick={clearFilters}
                    className="text-slate-600 hover:text-slate-900 text-sm flex items-center"
                  >
                    Clear filters
                    <ArrowRight className="ml-1 w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      {!searchTerm && !selectedCategory && featuredArticles.length > 0 && (
        <section className="py-16 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-12">
              <h2 className="text-3xl font-bold text-slate-900">Featured Articles</h2>
              <TrendingUp className="w-6 h-6 text-slate-400" />
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {featuredArticles.map((article, index) => (
                <Link 
                  key={article.id}
                  to={`/news/${article.slug}`}
                  className={`group ${index === 0 ? 'lg:col-span-2' : ''}`}
                >
                  <article className="h-full bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col">
                    {/* Fixed height image container */}
                    <div className={`relative ${index === 0 ? 'h-64 lg:h-80' : 'h-48'} bg-slate-100 overflow-hidden flex-shrink-0`}>
                      {article.featured_image ? (
                        <img
                          src={article.featured_image}
                          alt={article.title}
                          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className={`w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center ${article.featured_image ? 'hidden' : ''}`}>
                        <FileText className="w-16 h-16 text-slate-400" />
                      </div>
                    </div>

                    {/* Content with flex-grow to fill remaining space */}
                    <div className={`${index === 0 ? 'p-6 lg:p-8' : 'p-6'} flex flex-col flex-grow`}>
                      <div className="flex items-center gap-3 mb-4">
                        {article.category && (
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(article.category)}`}>
                            {article.category}
                          </span>
                        )}
                        {/* <div className="flex items-center text-slate-500 text-sm">
                          <Calendar className="w-4 h-4 mr-1" />
                          {formatDate(article.published_at || article.created_at)}
                        </div> */}
                        {article.reading_time && (
                          <div className="flex items-center text-slate-500 text-sm">
                            <Clock className="w-4 h-4 mr-1" />
                            {getReadingTime(article.reading_time)}
                          </div>
                        )}
                      </div>

                      <h3 className={`font-bold text-slate-900 mb-3 group-hover:text-slate-700 transition-colors ${index === 0 ? 'text-2xl lg:text-3xl' : 'text-xl'}`}>
                        {article.title}
                      </h3>

                      {article.excerpt && (
                        <p className={`text-slate-600 mb-4 line-clamp-3 ${index === 0 ? 'text-lg' : ''}`}>
                          {article.excerpt}
                        </p>
                      )}

                      {/* Push this to bottom with mt-auto */}
                      <div className="flex items-center justify-between mt-auto">
                        <div className="flex items-center text-slate-500 text-sm">
                          <User className="w-4 h-4 mr-1" />
                          {article.author || 'KGC Innovations'}
                        </div>
                        <div className="flex items-center text-slate-600 group-hover:text-slate-900 transition-colors">
                          <span className="text-sm font-medium">Read more</span>
                          <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Articles */}
      <section className="py-16 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <h2 className="text-3xl font-bold text-slate-900">
              {searchTerm || selectedCategory ? 'Search Results' : 'All Articles'}
            </h2>
            {filteredArticles.length > 0 && (
              <span className="text-slate-600">
                {filteredArticles.length} article{filteredArticles.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="bg-white rounded-2xl border border-slate-200 overflow-hidden animate-pulse">
                  <div className="h-48 bg-slate-200"></div>
                  <div className="p-6 space-y-4">
                    <div className="h-4 bg-slate-200 rounded w-1/3"></div>
                    <div className="h-6 bg-slate-200 rounded w-3/4"></div>
                    <div className="h-4 bg-slate-200 rounded w-full"></div>
                    <div className="h-4 bg-slate-200 rounded w-2/3"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredArticles.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article) => (
                <Link 
                  key={article.id}
                  to={`/news/${article.slug}`}
                  className="group h-full"
                >
                  <article className="h-full bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col">
                    {/* Fixed height image container */}
                    <div className="relative h-48 bg-slate-100 overflow-hidden flex-shrink-0">
                      {article.featured_image ? (
                        <img
                          src={article.featured_image}
                          alt={article.title}
                          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className={`w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center ${article.featured_image ? 'hidden' : ''}`}>
                        <FileText className="w-12 h-12 text-slate-400" />
                      </div>
                    </div>

                    {/* Content with flex-grow */}
                    <div className="p-6 flex flex-col flex-grow">
                      <div className="flex items-center gap-3 mb-4">
                        {article.category && (
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(article.category)}`}>
                            {article.category}
                          </span>
                        )}
                        <div className="flex items-center text-slate-500 text-sm">
                          <Calendar className="w-4 h-4 mr-1" />
                          {formatDate(article.published_at || article.created_at)}
                        </div>
                      </div>

                      {/* Title with fixed height using line-clamp */}
                      <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-slate-700 transition-colors line-clamp-2 min-h-[3.5rem]">
                        {article.title}
                      </h3>

                      {/* Excerpt with fixed height using line-clamp */}
                      {article.excerpt && (
                        <p className="text-slate-600 mb-4 line-clamp-3 min-h-[4.5rem]">
                          {article.excerpt}
                        </p>
                      )}

                      {/* Footer content pushed to bottom with mt-auto */}
                      <div className="mt-auto">
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-4 text-slate-500">
                            {article.reading_time && (
                              <div className="flex items-center">
                                <Clock className="w-4 h-4 mr-1" />
                                {getReadingTime(article.reading_time)}
                              </div>
                            )}
                            {article.views !== undefined && (
                              <div className="flex items-center">
                                <Eye className="w-4 h-4 mr-1" />
                                {article.views}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center text-slate-600 group-hover:text-slate-900 transition-colors">
                            <span className="font-medium">Read more</span>
                            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>

                        {/* Tags */}
                        {article.tags && article.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
                            {article.tags.slice(0, 3).map((tag, index) => (
                              <span key={index} className="flex items-center px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs">
                                <Tag className="w-3 h-3 mr-1" />
                                {tag}
                              </span>
                            ))}
                            {article.tags.length > 3 && (
                              <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs">
                                +{article.tags.length - 3}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No articles found</h3>
              <p className="text-slate-600 mb-6">
                {searchTerm || selectedCategory 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'No articles have been published yet.'
                }
              </p>
              {(searchTerm || selectedCategory) && (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center text-slate-600 hover:text-slate-900 font-medium"
                >
                  Clear filters <ArrowRight className="ml-1 w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default NewsPage;